import {
  Mutation,
  PinwheelFlowPageResultTypenames,
  Query,
  UpdateMYPResultTypenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { UpdateMYP } from 'services/graphql/mutations/UpdateMYP';
import PinwheelFlowTypename from 'services/graphql/queries/pinwheel/PinwheelFlowTypename.query';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

export const useUpdateMYP = (
  updatedMYPPercentage: string,
  onSaved: (isDirectDepositSetupRequired: boolean) => void,
) => {
  const router = useRouter();
  const [isErrorShow, setIsErrorShow] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [, updateMYP] = useCancellableMutation<Mutation>(UpdateMYP);
  const [executePinwheelFlowQuery] =
    useCancellableQuery<Query>(PinwheelFlowTypename);

  const updateMypPressEvent = usePressableAnalytics({
    analyticsId: 'mypUpdate',
    customAttributes: {
      move_your_pay_updated_percentage: updatedMYPPercentage,
      account_type: 'charge_card',
    },
  });

  const mypErrorEvent = usePressableAnalytics({
    analyticsId: 'updateMYPError',
    customAttributes: {
      move_your_pay_updated_percentage: updatedMYPPercentage,
    },
  });

  const leaveMYPScreen = () => {
    //JT-132713
    // The account ID vanishes from the URL on refresh, causing the navigation state to reset.
    // A separate ticket has been created to persist the account data or restore the navigation state after a reload.
    // For now, the user will be redirected to the home screen.
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/');
    }
  };

  const isDirectDepositSetupRequired = async () => {
    try {
      const data = await executePinwheelFlowQuery({});
      return (
        data.pinwheelFlowPage?.__typename ===
        PinwheelFlowPageResultTypenames.DdPinwheelSetupPage
      );
    } catch (error) {
      if (error instanceof AbortError) {
        throw error;
      }
      logBreadcrumb({
        level: 'error',
        message: `Direct deposit setup check failed ${error}`,
      });
      return false;
    }
  };

  const handleUpdateMYP = async () => {
    setIsUpdating(true);
    try {
      const percentage = parseFloat(updatedMYPPercentage);
      const [response, shouldShowSheet] = await Promise.all([
        updateMYP({ percentage }),
        percentage > 0 ? isDirectDepositSetupRequired() : false,
      ]);

      const data = response?.data?.updateMYP;
      if (data?.__typename === UpdateMYPResultTypenames.UpdateMYPSuccess) {
        updateMypPressEvent();
        onSaved(shouldShowSheet);
      } else {
        mypErrorEvent();
        setIsErrorShow(true);
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      mypErrorEvent();
      setIsErrorShow(true);
    }
    setIsUpdating(false);
  };

  return {
    handleUpdateMYP,
    updateMYPFetching: isUpdating,
    isErrorShow,
    setIsErrorShow,
    leaveMYPScreen,
  };
};
